import { faLoader } from '@fortawesome/pro-light-svg-icons';
import { faX } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal, ModalContent, Radio } from '@pickleballinc/react-ui';
import { useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-toastify';

import { sizeOfSheet } from '@/modules/league/new-components/constants';
import { useGetMatchInfosOnScoreSheets } from '@/modules/league/new-components/matches-components/hooks';
import { PrintForm } from '@/modules/league/new-components/matches-components/MatchesComponent/PrintForm/PrintForm';
import {
  GameDayInfo,
  MatchesResult,
  MatchInfosForScoreSheets,
} from '@/modules/league/new-components/types';

interface PrintScoreSheetModalProps {
  open: boolean;
  onClose: () => void;
  matchesResult: MatchesResult;
  headerContent: GameDayInfo;
}

const PrintScoreSheetModal = ({
  open,
  onClose,
  matchesResult,
  headerContent,
}: PrintScoreSheetModalProps) => {
  const { matchGroups } = matchesResult;
  const { details } = headerContent;

  const contentRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({ contentRef });

  const [poolsData, setPoolsData] = useState<Record<string, MatchInfosForScoreSheets[]>>({});
  const [sheetSize, setSheetSize] = useState('');
  const [loading, setLoading] = useState(false);

  const { mutateAsync: getMatchInfosOnScoreSheets } = useGetMatchInfosOnScoreSheets();

  useEffect(() => {
    const fetchPoolsData = async () => {
      try {
        setLoading(true);
        const data: Record<string, MatchInfosForScoreSheets[]> = {};
        for (const group of matchGroups) {
          const poolID = group.PoolID;
          const sequenceNumber = group.SequenceNumber || 1;

          const response = await getMatchInfosOnScoreSheets({
            matchDateId: details.MatchDateID,
            selectedPoolID: poolID,
            sequenceNumber,
          });

          const filteredResponse = response.filter(
            (match: MatchInfosForScoreSheets) =>
              match.DisplayMatchNumber > 0 && match.DisplayMatchNumber < 999999
          );

          if (group.PoolNumber) {
            data[group.PoolNumber] = filteredResponse;
          }
        }
        setPoolsData(data);
      } catch (err) {
        toast.error(
          err instanceof Error
            ? err.message
            : 'Something wrong happened and we were not able to load score sheets.'
        );
      } finally {
        setLoading(false);
      }
    };

    if (matchGroups?.length > 0) {
      fetchPoolsData();
    }
  }, [details.MatchDateID]);

  return (
    <>
      <Modal open={open} onOpenChange={onClose}>
        <ModalContent className='flex w-[calc(100%-16px)] flex-col gap-4 !overflow-hidden !p-6 !outline-none  md:max-w-[480px]'>
          <div className='relative text-center'>
            <div
              className='absolute -right-2 top-0 flex size-6 items-center justify-center rounded-md border border-solid border-gray-300 bg-white p-1 hover:cursor-pointer hover:bg-gray-100'
              onClick={onClose}
            >
              <FontAwesomeIcon icon={faX} className='text-gray-400' height={20} />
            </div>
          </div>
          <div className='flex flex-col gap-4'>
            <div className='text-center text-xl font-semibold text-gray-900 md:text-2xl'>
              Print All Matches
            </div>
            <div className='flex flex-col gap-2'>
              <Radio
                key={'Simplified Score Sheet'}
                name='printSheetType'
                id={'Simplified Score Sheet'}
                value={'Simplified Score Sheet'}
                Text={'Simplified Score Sheet'}
                defaultChecked={true}
              />
              <div className='text-sm font-semibold leading-4 text-gray-900 md:text-lg'>
                Print sheet size
              </div>
              {sizeOfSheet.map((option) => (
                <Radio
                  key={`printSheetSize-${option.value}`}
                  name='printSheetSize'
                  id={option.label}
                  value={option.label}
                  Text={option.label}
                  onChange={(event) => setSheetSize(event.currentTarget.value)}
                />
              ))}
              <div className='flex flex-col gap-4 py-4 text-sm leading-4 text-gray-800 md:text-base'>
                <p>Half sheets printed each match on a separate sheet. One per 8.5 X 5.5 area.</p>
                <p>Full sheets print 2 matches on an 8.5 X 11 sheet of paper.</p>
              </div>
            </div>
          </div>
          <div className='flex items-center justify-between gap-4'>
            <Button
              variant='secondary'
              className='flex w-[200px] justify-center border !text-sm !text-gray-500 focus:!shadow-[unset]'
              onClick={onClose}
            >
              Close
            </Button>

            <Button
              variant='primary'
              className='flex w-[200px] disabled:!bg-leagues-300/50 !bg-leagues-300 justify-center !text-sm focus:!shadow-[unset]'
              disabled={!sheetSize || loading}
              onClick={() => {
                handlePrint();
              }}
              prefixIcon={loading && <FontAwesomeIcon icon={faLoader} className='animate-spin' />}
            >
              {loading ? 'Loading..' : 'Print'}
            </Button>
          </div>
        </ModalContent>
      </Modal>

      {sheetSize && poolsData && (
        <PrintForm
          innerRef={contentRef}
          headerContent={headerContent}
          matchGroups={matchGroups}
          sheetSize={sheetSize}
          poolsData={poolsData}
        />
      )}
    </>
  );
};

export default PrintScoreSheetModal;
